
import { defineComponent } from 'vue'
import VuePdfEmbed from 'vue-pdf-embed'
import axios from 'axios'
import { store } from '@/store'
import type { InstructionWithFile, InstructionConfirmation } from '@/store/instruction'
import { renderInstructionMarkdown } from '@/utils/instructionMarkdown'
import { printInstructionConfirmation } from '@/utils/printCertificate'

interface ViewSection {
  sectionId: string
  title: string
  fileName: string
  fileType: 'pdf' | 'docx' | 'md'
  fileUrl: string | null
}

export default defineComponent({
  name: 'InstructionView',
  components: { VuePdfEmbed },

  data() {
    return {
      loading: true,
      saving: false,
      error: '',
      inited: false,
      instruction: null as InstructionWithFile | null,
      checked: [] as boolean[],
      readSections: [] as boolean[],
      activeTab: 0,
      docLoading: true,
      // Gerendertes HTML der Markdown-Sektionen, Index = Tab-Position.
      mdHtml: {} as Record<number, string>,
    }
  },

  computed: {
    instructionId(): string {
      return this.$route.params.instructionId as string
    },
    // In einem frisch geöffneten Tab (Vorschau, Mail-Link) mountet die Seite, bevor
    // getUserDate → organisation/LOAD die OrgId und Session gesetzt haben. Erst wenn
    // beides da ist, dürfen die Initial-Loads laufen — sonst laufen sie ins Leere
    // und die Seite bleibt fälschlich auf „Unterweisung nicht gefunden" stehen.
    bootReady(): boolean {
      return !!(store.getters['organisation/getOrganisationId'] && store.getters.getJWT)
    },
    signatureName(): string {
      const user = store.getters.getUser
      return user ? `${user.first_name} ${user.last_name}` : ''
    },
    // Sektionen aus der Auflösung; Fallback auf ein Einzeldokument (Legacy/alte Antwort).
    sections(): ViewSection[] {
      const ins = this.instruction
      if (!ins) return []
      const resolved = (ins as any).resolvedSections as any[] | undefined
      if (resolved && resolved.length) {
        return resolved.map((s) => ({
          sectionId: s.sectionId,
          title: s.title,
          fileName: s.fileName,
          fileType: s.fileType,
          fileUrl: s.fileUrl,
        }))
      }
      if (ins.fileKey || ins.fileUrl) {
        return [{
          sectionId: 'legacy',
          title: ins.title,
          fileName: ins.fileName,
          fileType: ins.fileType,
          fileUrl: ins.fileUrl,
        }]
      }
      return []
    },
    activeSection(): ViewSection | null {
      return this.sections[this.activeTab] || null
    },
    confirmationItems(): string[] {
      return this.instruction?.confirmationItems || []
    },
    // Unterzeile im Kopf: vergebene Dokumenttitel, nie der rohe Dateiname.
    // Trägt das einzige Dokument denselben Titel wie die Unterweisung, entfällt die Zeile.
    sectionSummary(): string {
      const n = this.sections.length
      if (n === 0) return ''
      if (n === 1) {
        const title = this.sections[0].title
        return title && title !== this.instruction?.title ? title : ''
      }
      return `${n} Dokumente`
    },
    allChecked(): boolean {
      const items = this.confirmationItems
      if (!items.length) return true
      return items.every((_, idx) => this.checked[idx] === true)
    },
    allRead(): boolean {
      return this.sections.every((_, idx) => !this.needsScrollForIdx(idx) || this.readSections[idx] === true)
    },
    myConfirmation(): InstructionConfirmation | undefined {
      return store.getters['instruction/getMyConfirmation'](this.instructionId)
    },
  },

  methods: {
    goBack() {
      this.$router.push({ name: 'lmsCatalog' })
    },

    // Scroll-Pflicht für inline angezeigte Dokumente (PDF und Editor-Dokumente).
    needsScrollForIdx(idx: number): boolean {
      const s = this.sections[idx]
      return !!(s && (s.fileType === 'pdf' || s.fileType === 'md') && s.fileUrl)
    },

    setDocRef(el: any, idx: number) {
      if (el) (this as any).docEls[idx] = el as HTMLElement
    },

    onDocScroll(e: Event, idx: number) {
      const el = e.target as HTMLElement
      // 24px Toleranz, damit das Ende zuverlässig erkannt wird
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
        this.markRead(idx)
      }
    },

    onDocRendered(idx: number) {
      this.docLoading = false
      this.$nextTick(() => this.evaluateScroll(idx))
    },

    onDocFailed() {
      this.docLoading = false
    },

    evaluateScroll(idx: number) {
      const el = (this as any).docEls[idx] as HTMLElement | undefined
      // Nur sichtbare Tabs prüfen — versteckte (display:none) haben Höhe 0 und dürfen
      // nicht fälschlich als gelesen gelten.
      if (!el || el.offsetParent === null) return
      // Passt das Dokument ohne Scrollbalken, gilt es als gelesen.
      if (el.scrollHeight <= el.clientHeight + 24) {
        this.markRead(idx)
      }
    },

    markRead(idx: number) {
      if (this.readSections[idx] !== true) this.readSections.splice(idx, 1, true)
    },

    formatDate(ts: number): string {
      if (!ts) return ''
      const d = new Date(ts)
      return d.toLocaleDateString('de-DE') + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    },

    async confirm() {
      this.error = ''
      if (!this.instruction || !this.allChecked) return
      this.saving = true
      try {
        const checkedItems = this.confirmationItems.map((label, idx) => ({
          label,
          checked: this.checked[idx] === true,
        }))
        await store.dispatch('instruction/CONFIRM', {
          instructionId: this.instructionId,
          payload: {
            checkedItems,
            signatureName: this.signatureName,
            userName: this.signatureName,
          },
        })
      } catch (e: any) {
        this.error = e?.response?.data?.error || 'Die Bestätigung konnte nicht gespeichert werden.'
      } finally {
        this.saving = false
      }
    },

    // Nachweis als Druckansicht (Browser: "Als PDF speichern").
    downloadCertificate() {
      const c = this.myConfirmation
      if (!c || !this.instruction) return
      printInstructionConfirmation({
        orgName: store.getters['organisation/getOrganisation']?.name || '',
        employeeName: c.userName || c.signatureName,
        signatureName: c.signatureName,
        instructionTitle: this.instruction.title,
        documentTitles: this.sections.map((s) => s.title || s.fileName),
        confirmedAt: c.confirmedAt,
        checkedItems: c.checkedItems,
      })
    },

    // Markdown-Quelltexte der Editor-Sektionen laden und gerendert ablegen.
    // Platzhalter (z. B. Einrichtungsname) werden dabei ersetzt.
    async loadMarkdownSections() {
      const orgName = store.getters['organisation/getOrganisation']?.name || ''
      await Promise.all(this.sections.map(async (s, idx) => {
        if (s.fileType !== 'md' || !s.fileUrl) return
        try {
          const { data } = await axios.get(s.fileUrl, { responseType: 'text', transformResponse: [(x) => x] })
          this.mdHtml = { ...this.mdHtml, [idx]: renderInstructionMarkdown(String(data), { orgName }) }
        } catch (e) {
          this.mdHtml = { ...this.mdHtml, [idx]: '<p>Das Dokument konnte nicht geladen werden.</p>' }
        }
        if (idx === this.activeTab) this.docLoading = false
        // Nach dem Rendern prüfen, ob die Sektion überhaupt scrollbar ist.
        this.$nextTick(() => this.evaluateScroll(idx))
      }))
    },

    async init() {
      if (this.inited) return
      this.inited = true
      try {
        // Nicht-fatal: Scheitert nur der Bestätigungs-Abruf, soll die Unterweisung
        // trotzdem angezeigt werden (dann eben im unbestätigten Zustand).
        await store.dispatch('instruction/LOAD_MY_CONFIRMATIONS', { force: true }).catch(() => undefined)
        this.instruction = await store.dispatch('instruction/SHOW', this.instructionId)
        this.checked = this.confirmationItems.map(() => false)
        this.readSections = this.sections.map(() => false)
        const a = this.activeSection
        this.docLoading = !!(a && (a.fileType === 'pdf' || a.fileType === 'md') && a.fileUrl)
        this.loadMarkdownSections()
        // Sicherheitsnetz, falls das @rendered-Event ausbleibt: kurz nach dem Mount prüfen,
        // ob die Dokumente überhaupt scrollbar sind (sonst gelten sie als gelesen).
        this.$nextTick(() => {
          this.sections.forEach((_, idx) => this.evaluateScroll(idx))
          setTimeout(() => this.sections.forEach((_, idx) => this.evaluateScroll(idx)), 1500)
        })
      } catch (e) {
        this.instruction = null
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    // Startet die Initial-Loads nach, sobald der Boot (OrgId + Session) fertig ist —
    // deckt den Fall ab, dass die Seite vor Abschluss des Boots gemountet wurde.
    bootReady(ready: boolean) {
      if (ready) this.init()
    },
    // Beim Tab-Wechsel den Scroll zurücksetzen. Die „gelesen"-Prüfung übernimmt das
    // @rendered-Event des neu gemounteten PDFs (sonst würde mit veralteter Höhe geprüft).
    activeTab() {
      const s = this.activeSection;
      // Loader zeigen, solange ein PDF rendert oder ein Editor-Dokument noch lädt.
      this.docLoading = !!(s && s.fileUrl && (
        s.fileType === 'pdf' ||
        (s.fileType === 'md' && this.mdHtml[this.activeTab] === undefined)
      ));
      this.$nextTick(() => {
        const el = (this as any).docEls[this.activeTab] as HTMLElement | undefined;
        if (el) el.scrollTop = 0;
      });
    },
  },

  created() {
    // Nicht-reaktiver Halter für die Scroll-Container-Elemente (keine Proxy-Wrapper auf DOM-Knoten).
    (this as any).docEls = {}
  },

  async mounted() {
    if (this.bootReady) await this.init()
  },
})
